"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  GetPartyChatResponse,
  PartyChatAccess,
  PartyChatMessage,
  PartyChatUserMessage,
} from "../types";
import {
  PARTY_CHAT_POLL_INTERVAL_MS,
  validatePartyChatMessage,
} from "../party-chat";

export interface UsePartyChatOptions {
  fetchMessages: (partyId: string) => Promise<GetPartyChatResponse>;
  sendMessage: (partyId: string, body: string) => Promise<PartyChatUserMessage>;
  pollIntervalMs?: number;
  enabled?: boolean;
  initialData?: GetPartyChatResponse | null;
}

export interface UsePartyChatResult {
  access: PartyChatAccess | null;
  messages: PartyChatMessage[];
  currentUserId: string | null;
  loading: boolean;
  error: string | null;
  sendError: string | null;
  sending: boolean;
  refetch: () => Promise<void>;
  send: (body: string) => Promise<boolean>;
}

export function usePartyChat(
  partyId: string,
  options: UsePartyChatOptions,
): UsePartyChatResult {
  const {
    fetchMessages,
    sendMessage,
    pollIntervalMs = PARTY_CHAT_POLL_INTERVAL_MS,
    enabled = true,
    initialData = null,
  } = options;

  const [access, setAccess] = useState<PartyChatAccess | null>(
    initialData?.access ?? null,
  );
  const [messages, setMessages] = useState<PartyChatMessage[]>(
    initialData?.messages ?? [],
  );
  const [currentUserId, setCurrentUserId] = useState<string | null>(
    initialData?.currentUserId ?? null,
  );
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(initialData?.error ?? null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const fetchMessagesRef = useRef(fetchMessages);
  fetchMessagesRef.current = fetchMessages;
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const requestSeqRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(async () => {
    if (!partyId || !enabled) {
      setLoading(false);
      return;
    }

    const seq = ++requestSeqRef.current;
    try {
      const data = await fetchMessagesRef.current(partyId);
      if (!mountedRef.current || seq !== requestSeqRef.current) {
        return;
      }
      setAccess(data.access);
      setMessages(data.messages);
      setCurrentUserId(data.currentUserId);
      setError(data.error);
    } catch (err) {
      if (!mountedRef.current || seq !== requestSeqRef.current) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load chat");
    } finally {
      if (mountedRef.current && seq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, partyId]);

  useEffect(() => {
    if (!enabled || !partyId) {
      return;
    }

    void refetch();

    if (!pollIntervalMs || pollIntervalMs <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      void refetch();
    }, pollIntervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [enabled, partyId, pollIntervalMs, refetch]);

  const send = useCallback(
    async (body: string): Promise<boolean> => {
      const validated = validatePartyChatMessage(body);
      if (!validated.ok) {
        setSendError(validated.error);
        return false;
      }

      setSending(true);
      setSendError(null);
      try {
        const message = await sendMessageRef.current(partyId, validated.body);
        if (!mountedRef.current) {
          return true;
        }
        setMessages((previous) => {
          if (previous.some((existing) => existing.id === message.id)) {
            return previous;
          }
          return [...previous, message];
        });
        return true;
      } catch (err) {
        if (mountedRef.current) {
          setSendError(
            err instanceof Error ? err.message : "Failed to send message",
          );
        }
        return false;
      } finally {
        if (mountedRef.current) {
          setSending(false);
        }
      }
    },
    [partyId],
  );

  return {
    access,
    messages,
    currentUserId,
    loading,
    error,
    sendError,
    sending,
    refetch,
    send,
  };
}
